import React from "react";
import type { QueryFilters, QueryClient, Query } from "@tanstack/react-query";
import { useQuery as useReactQuery, useQueryClient } from "@tanstack/react-query";
import type { Input, Output } from "./call";
import call from "./call";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import isFuncState from "../lib/isFuncState";
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type { EndpointTree } from "../../../service-diagnostics-core/src/Endpoint";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import stringify from "fast-json-stable-stringify";
import debounce from "lodash.debounce";
import deepEqual from "fast-deep-equal";

const DEFAULT_METHOD = "GET" as const;
type DEFAULT_METHOD = typeof DEFAULT_METHOD;

type GetRequired<T> = {
  [K in keyof T as T[K] extends Required<T>[K] ? K : never]: T[K];
};
// returns true for { test?: 'toto' } and {}, false for { test: 'toto' }
type IsPossiblyEmptyObject<Obj extends Record<PropertyKey, unknown>> = [
  keyof GetRequired<Obj>,
] extends [never]
  ? true
  : false;

const SECONDS_MS = 1000;
const MINUTE_MS = 60 * SECONDS_MS;

type Method = DEFAULT_METHOD | "POST" | "PATCH" | "PUT" | "DELETE";
type Path<M extends Method> = keyof EndpointTree[M];
type InputWithMethod<M extends Method, P extends Path<M>> = Input<M, P> & { method?: M };
type QueryKey<M extends Method, P extends Path<M>> = [
  M,
  P,
  Input<M, P> extends { routeParams: infer RP } ? RP : Record<string, never>,
  Input<M, P> extends { query: infer Q } ? Q : Record<string, never>,
  Input<M, P> extends { body: infer B } ? B : Record<string, never>,
];

const emptyObject = Object.freeze({});
function useQueryKey<M extends Method, P extends Path<M>>(
  method: M,
  path: P,
  input: {
    routeParams?: Record<string, unknown>;
    query?: Record<string, unknown>;
    body?: unknown;
  },
): QueryKey<M, P> {
  const { routeParams, query, body } = input;
  const stableRouteParams = useDistinctShape(routeParams ?? emptyObject);
  const stableQuery = useDistinctShape(query ?? emptyObject);
  const stableBody = useDistinctShape(body ?? emptyObject);
  return React.useMemo(
    () => [method, path, stableRouteParams, stableQuery, stableBody] as QueryKey<M, P>,
    [method, path, stableRouteParams, stableQuery, stableBody],
  );
}

function getQueryKey<M extends Method, P extends Path<M>>(
  method: M,
  path: P,
  input: {
    routeParams?: Record<string, unknown>;
    query?: Record<string, unknown>;
    body?: unknown;
  },
): QueryKey<M, P> {
  const { routeParams, query, body } = input;
  const stableRouteParams = routeParams ?? emptyObject;
  const stableQuery = query ?? emptyObject;
  const stableBody = body ?? emptyObject;
  return [method, path, stableRouteParams, stableQuery, stableBody] as QueryKey<M, P>;
}

function queryKeyPredicate(invalidationKey: unknown[]) {
  return function (query: Query): boolean {
    if (!Array.isArray(query.queryKey)) throw new Error("query.queryKey is not an array");

    let index = -1;
    for (const invalidationKeyPart of invalidationKey) {
      index++;
      const queryKeyPart: unknown = query.queryKey[index];
      if (invalidationKeyPart === undefined) {
        // good, can be anything
      } else if (typeof invalidationKeyPart === "string") {
        if (typeof queryKeyPart !== "string")
          throw new Error(`Cannot compare ${invalidationKeyPart} with ${queryKeyPart}`);
        if (queryKeyPart !== invalidationKeyPart) return false;
      } else if (typeof invalidationKeyPart === "object" && invalidationKeyPart !== null) {
        if (typeof queryKeyPart !== "object" || queryKeyPart === null)
          throw new Error(`Cannot compare ${invalidationKeyPart} with ${queryKeyPart}`);

        // check all values of the invalidationKeyPart with the queryKeyPart
        for (const key of Object.keys(invalidationKeyPart)) {
          if (
            !deepEqual(
              (queryKeyPart as { [k: string]: unknown })[key],
              (invalidationKeyPart as { [k: string]: unknown })[key],
            )
          )
            return false;
        }
      } else {
        throw new Error(
          `invalidationKeyPart is not a string, undefined or object: ${invalidationKeyPart}`,
        );
      }
    }
    return true;
  };
}

export function useGetQueryData<P extends Path<M>, M extends Method = DEFAULT_METHOD>(
  ...[path, { method = DEFAULT_METHOD as M, ...input } = emptyObject]: IsPossiblyEmptyObject<
    InputWithMethod<M, P>
  > extends true
    ? [path: P, options?: InputWithMethod<M, P>]
    : [path: P, options: InputWithMethod<M, P>]
): () => Output<M, P> | undefined {
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(method, path, input);

  return React.useCallback(() => queryClient.getQueryData(queryKey), [queryKey, queryClient]);
}

// does not trigger data loading
export function useQueryObserver<P extends Path<M>, M extends Method = DEFAULT_METHOD>(
  ...[path, { method = DEFAULT_METHOD as M, ...input } = {}]: IsPossiblyEmptyObject<
    InputWithMethod<M, P>
  > extends true
    ? [path: P, options?: InputWithMethod<M, P>]
    : [path: P, options: InputWithMethod<M, P>]
): Output<M, P> | undefined {
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(method, path, input);
  const queryCache = queryClient.getQueryCache();
  const getData = React.useCallback(
    () => queryCache.find<Output<M, P>>(queryKey)?.state.data,
    [queryKey, queryCache],
  );
  const [data, setData] = React.useState(getData);
  const stableData = useDistinctShape(data);
  const debouncedSetData = useDebouncedCallback(setData, [setData], 100, {
    leading: true,
    trailing: true,
    maxWait: 100,
  });

  React.useEffect(() => {
    const unsubscribe = queryCache.subscribe(event => {
      if (
        event.query.queryKey[0] === queryKey[0] &&
        (event.type === "updated" || event.type === "added" || event.type === "removed") &&
        stringify(event.query.queryKey) === stringify(queryKey)
      ) {
        debouncedSetData(event.query.state.data);
      }
    });

    return unsubscribe;
  }, [debouncedSetData, queryKey, queryCache]);

  return stableData;
}

export function usePrefetchQueryFn(): <P extends Path<M>, M extends Method = DEFAULT_METHOD>(
  ...[path, options]: IsPossiblyEmptyObject<InputWithMethod<M, P>> extends true
    ? [path: P, options?: InputWithMethod<M, P>]
    : [path: P, options: InputWithMethod<M, P>]
) => Promise<void> | undefined {
  const queryClient = useQueryClient();
  return React.useCallback(
    <P extends Path<M>, M extends Method = DEFAULT_METHOD>(
      ...[path, { method = DEFAULT_METHOD as M, ...input } = {}]: IsPossiblyEmptyObject<
        InputWithMethod<M, P>
      > extends true
        ? [path: P, options?: InputWithMethod<M, P>]
        : [path: P, options: InputWithMethod<M, P>]
    ) => {
      const queryKey = getQueryKey(method, path, input);
      if (queryClient.getQueryData(queryKey) !== undefined) return;

      return queryClient.prefetchQuery(queryKey, ({ signal }) =>
        call(method, path, input, {
          signal,
          jsonStreamCallback: createJsonStreamCallback(queryClient, queryKey),
        }),
      );
    },
    [queryClient],
  );
}

export function useRevalidate(
  options?: QueryFilters,
): <P extends Path<M>, M extends Method = DEFAULT_METHOD>(
  path: P,
  routeParams?: Partial<QueryKey<M, P>[2]>,
  query?: Partial<QueryKey<M, P>[3]>,
  body?: Partial<QueryKey<M, P>[4]>,
  method?: M,
) => Promise<void> {
  const queryClient = useQueryClient();
  const stableOptions = useDistinctShape(options ?? {});

  return React.useCallback(
    async <P extends Path<M>, M extends Method = DEFAULT_METHOD>(
      path: P,
      routeParams: Partial<QueryKey<M, P>[2]> = emptyObject,
      query: Partial<QueryKey<M, P>[3]> = emptyObject,
      body: Partial<QueryKey<M, P>[4]> = emptyObject,
      method = DEFAULT_METHOD as M,
    ) => {
      const invalidationKey = [method, path, routeParams, query, body];
      await queryClient.invalidateQueries({
        ...stableOptions,
        predicate: queryKeyPredicate(invalidationKey),
      });
    },
    [queryClient, stableOptions],
  );
}

function createJsonStreamCallback<P extends Path<M>, M extends Method>(
  queryClient: QueryClient,
  queryKey: QueryKey<M, P>,
) {
  let cb: number | undefined;
  return debounce(
    (body: Output<M, P>["body"], done: boolean) => {
      if (cb !== undefined) window.cancelIdleCallback(cb);
      cb = window.requestIdleCallback(() =>
        queryClient.setQueryData<Output<M, P>>(queryKey, oldData => {
          const streaming = !done;
          if (oldData === undefined) throw new Error("oldData is undefined");
          if (oldData.body === body && oldData.streaming === streaming) return;
          return { ...oldData, body, streaming };
        }),
      );
    },
    300,
    { leading: false, trailing: true, maxWait: 1000 },
  );
}

type UseQueryOptions<M extends Method, P extends Path<M>, Suspend, Disabled> = InputWithMethod<
  M,
  P
> & {
  placeholderData?: Output<M, P>;
  keepPreviousData?: boolean;
  keepFresh?: boolean;
  suspend?: Suspend;
  disabled?: Disabled;
};

type UseQueryOutput<M extends Method, P extends Path<M>, Suspend, Disabled> = Disabled extends true
  ? undefined
  : Suspend extends true
    ? Output<M, P>
    : Output<M, P> | undefined;

function useQuery<
  P extends Path<M>,
  M extends Method = DEFAULT_METHOD,
  Disabled extends boolean = false,
  Suspend extends boolean = true,
>(
  ...[path, options = emptyObject]: IsPossiblyEmptyObject<
    UseQueryOptions<M, P, Suspend, Disabled>
  > extends true
    ? [path: P, options?: UseQueryOptions<M, P, Suspend, Disabled>]
    : [path: P, options: UseQueryOptions<M, P, Suspend, Disabled>]
): UseQueryOutput<M, P, Suspend, Disabled> {
  const {
    method = DEFAULT_METHOD as M,
    placeholderData = undefined,
    keepPreviousData = false,
    keepFresh = false,
    disabled = false as Disabled,
    suspend = true as Suspend,
    ...input
  } = options;
  const queryClient = useQueryClient();
  const queryKey: ReactQueryParams[0] = useQueryKey(method, path, input);

  const useTypedReactQuery = useReactQuery<Output<M, P>, never, Output<M, P>, QueryKey<M, P>>;
  type ReactQueryParams = Parameters<typeof useTypedReactQuery>;

  const queryFn: ReactQueryParams[1] = React.useCallback(
    ({ signal }: { signal?: AbortSignal }) =>
      call(method, path, input, {
        signal,
        jsonStreamCallback: createJsonStreamCallback(queryClient, queryKey),
      }),
    [input, queryKey, path, queryClient, method],
  );

  const rqOptions = React.useMemo<ReactQueryParams[2]>(
    () => ({
      suspense: suspend,
      staleTime: keepFresh ? 2 * SECONDS_MS : 200 * MINUTE_MS, // keepfresh: data is marked as staled after 10 seconds, next mount after that will refetch it. It's like a debounced refetchOnMount: 'always'
      keepPreviousData,
      placeholderData,
      refetchInterval: keepFresh ? 10 * MINUTE_MS : false, // refetch every 10 minutes to avoid very old data on "static" sessions where the window is focused but the user is not interacting with the app
      refetchOnWindowFocus: keepFresh, // refetch when the browser window regains focus
      enabled: !disabled,
    }),
    [disabled, keepFresh, keepPreviousData, placeholderData, suspend],
  );

  const result = useTypedReactQuery(queryKey, queryFn, rqOptions);
  const { data } = result;

  if (disabled) return undefined as UseQueryOutput<M, P, Suspend, Disabled>;
  if (suspend && data === undefined)
    throw new Error(`suspense mode: data is undefined in useQuery(${String(path)})`);

  return data as UseQueryOutput<M, P, Suspend, Disabled>;
}

export function useMutateBody<P extends Path<M>, M extends Method = DEFAULT_METHOD>(
  ...[path, { method = DEFAULT_METHOD as M, ...input } = emptyObject]: IsPossiblyEmptyObject<
    InputWithMethod<M, P>
  > extends true
    ? [path: P, options?: InputWithMethod<M, P>]
    : [path: P, options: InputWithMethod<M, P>]
): (newData: React.SetStateAction<Output<M, P>["body"]>) => () => void {
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(method, path, input);

  return React.useCallback(
    (newData: React.SetStateAction<Output<M, P>["body"]>) => {
      const old = queryClient.getQueryData<Output<M, P>>(queryKey);
      if (!old) return () => void 0; // we don't mutate non-initialized state
      queryClient.setQueryData<Output<M, P>>(queryKey, {
        ...old,
        body: isFuncState(newData) ? newData(old.body) : newData,
      });

      // return rollback function
      return function rollback() {
        queryClient.setQueryData(queryKey, old);
      };
    },
    [queryKey, queryClient],
  );
}

export function useMutate<P extends Path<M>, M extends Method = "GET">(
  ...[path, { method = "GET" as M, ...input } = emptyObject]: IsPossiblyEmptyObject<
    InputWithMethod<M, P>
  > extends true
    ? [path: P, options?: InputWithMethod<M, P>]
    : [path: P, options: InputWithMethod<M, P>]
): (newData: React.SetStateAction<Output<M, P>>) => () => void {
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(method, path, input);

  return React.useCallback(
    (newData: React.SetStateAction<Output<M, P>>) => {
      const old = queryClient.getQueryData<Output<M, P>>(queryKey);
      if (!old) return () => void 0; // we don't mutate non-initialized state
      queryClient.setQueryData<Output<M, P>>(
        queryKey,
        isFuncState(newData) ? newData(old) : newData,
      );

      return function rollback() {
        queryClient.setQueryData(queryKey, old);
      };
    },
    [queryKey, queryClient],
  );
}

export default useQuery;
